import { MutableState } from 'src/replicant/State';
import { getCurrentLeagueId, getLeagueReward } from './leagues.getters';
import { grantMatch2Rewards } from '../match2.modifiers';
import { leagueExpirationTime } from './leagues.ruleset';
import { match2MaxLevelChangedAt } from '../match2.ruleset';
import { CurrencyReplicantAPI } from 'src/replicant/inventory/inventory.utils';
import { Feature, sendAnalyticsEvents } from 'src/replicant/analytics';
import { FixedGemsReward } from '../rewards/match2-rewards.ruleset';

/**
 * Join league.
 * @param state
 * @param now
 * @param leagueId
 * @returns
 */
export function joinLeague(
  state: MutableState,
  now: number,
  leagueId: string = getCurrentLeagueId(state),
): { leagueId: string } {
  // Already joined. Bail.
  if (state.match2.leagues[leagueId]) return { leagueId };

  // Create league.
  state.match2.leagues[leagueId] = {
    tier: 'master',
    phase: 1,
    score: 0,
    claimed: false,
    modifiedAt: now,
    createdAt: now,
  };

  return { leagueId };
}

/**
 * Add league score.
 * @param state
 * @param score
 * @param now
 * @param leagueId
 * @returns
 */
export function addLeagueScore(
  state: MutableState,
  score: number,
  now: number,
  leagueId: string = getCurrentLeagueId(state),
): void {
  // Join league if not already joined.
  joinLeague(state, now, leagueId);

  // Update score.
  const league = state.match2.leagues[leagueId];
  league.score = league.score + score;
  league.modifiedAt = now;

  // Update all time score.
  state.match2.leaguesAllTimeScore += score;

  // Advance the curated level position only for the current league.
  // Scoring an older league did not serve the level at the current cursor.
  if (leagueId === getCurrentLeagueId(state)) {
    state.match2.leaguePhaseCursor += score;
  }
}

/**
 * Finish league.
 * @param state
 * @param finalRank
 * @param now
 * @param leagueId
 * @returns
 */
export function finishLeague(
  state: MutableState,
  finalRank: number,
  now: number,
  leagueId: string,
): void {
  const league = state.match2.leagues[leagueId];

  // League doesn't exist. Bail.
  if (!league) return;

  // Already have a final rank. Can happen if two players try to finish the event at the same time.
  if (league.finalRank) return;

  // Update final rank.
  league.finalRank = finalRank;
  league.modifiedAt = now;
}

/**
 * Claim league rewards.
 * @param state
 * @param leagueId
 * @param now
 * @returns
 */
export function claimLeagueRewards(
  state: MutableState,
  leagueId: string,
  api: CurrencyReplicantAPI,
): void {
  const league = state.match2.leagues[leagueId];

  // League doesn't exist. Throw error.
  if (!league) {
    throw new Error(
      `Cannot claim rewards for league ${leagueId} because it does not exist.`,
    );
  }

  // League not finished. Throw error.
  if (!league.finalRank) {
    throw new Error(
      `Cannot claim rewards for league ${leagueId} because it has not finished yet.`,
    );
  }

  // League already claimed. Throw error.
  if (league.claimed) {
    throw new Error(
      `Cannot claim rewards for league ${leagueId} because it has already been claimed.`,
    );
  }

  // Grant rewards.
  const rewards = getLeagueReward(league.finalRank);
  if (rewards) {
    grantMatch2Rewards(
      state,
      rewards,
      {
        feature: Feature.MATCH2.LEAGUES._,
        $subFeature: Feature.MATCH2.LEAGUES.REWARDS,
      },
      api,
    );

    const gems =
      (rewards.find((r) => r.type === 'gems') as FixedGemsReward)?.amount ?? 0;

    // Send analytics event.
    sendAnalyticsEvents(api, [
      {
        eventType: 'ChampionsLeaderboardReward',
        eventProperties: {
          type: 'seasonEnd',
          reward: gems,
        },
      },
    ]);
  }

  // Mark league as claimed.
  state.match2.leagues[leagueId].claimed = true;
}

/**
 * Cleanup old leagues.
 * @param state
 * @param now
 * @returns
 */
export function cleanupOldLeagues(state: MutableState, now: number): void {
  const leagues = state.match2.leagues;

  // Get all leagues that are older than expiration time.
  const oldLeagues = Object.keys(leagues).filter(
    (id) =>
      id !== getCurrentLeagueId(state) && // Don't include current league.
      now - match2MaxLevelChangedAt.getTime() >= leagueExpirationTime && // Only cleanup leagues 90 days after new levels were introduced.
      leagues[id].createdAt < match2MaxLevelChangedAt.getTime() && // Only include leagues that were created before new levels were introduced.
      (!leagues[id].finalRank || leagues[id].claimed), // Skip leagues that are not claimed yet and can still be.
  );

  // Delete old leagues.
  oldLeagues.forEach((id) => delete leagues[id]);
}
